import { onDatalayerDownloadFile } from '@/features/FilteredList/FilteredList.datalayer';
import ElasticAdapter from '@/features/shared/Adapters/ElasticAdapter';
import CMSWrapper from '@/features/shared/CMSWrapper';
import ContentContainer from '@/features/shared/ContentContainer';
import ElasticComponentContext, {
  ElasticComponentContextType,
} from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { useBotSearchParams, useElasticSearch } from '@/features/shared/Hooks';
import { parseSortByPublicationDate } from '@/features/shared/Hooks/elasticSearch/useElasticSearch.utils';
import { MuiContainer } from '@/features/shared/material-ui';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { ColorNames, LoadingStatusValues, SpacingSizes } from '@/utils/constants';
import { readCsvLine } from '@/utils/helpers';
import { resolveRTC, resolveSortBy, resolveStatus, resolveTaxonomyFacets } from '@/utils/resolvers';
import { BrPageContext } from '@bloomreach/react-sdk';
import { merge, pick } from 'lodash/fp';
import qs from 'query-string';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SearchResult from './SearchResult';
import {
  mapTarget,
  onDatalayer,
  onDatalayerCustomFilters,
  onDatalayerTriggerSearchResult,
  onDatalayerCustomSort,
} from './SearchResult.datalayer';
import {
  adaptResults,
  elasticSearchAuthDefaultFields,
  encodeWithCustomFilters,
  partnerOnlyFacetsWithLanguageModifier,
  SEARCH_RESULT_ELASTIC_DEFAULT_FIELDS,
} from './SearchResult.utils';
import { withGTMPipe } from '@/features/shared/HOCs/withGTM';

const TAX_LANGUAGE_MAP: Record<string, string> = {
  du: 'Dutch',
  en: 'English',
  fr: 'French',
  de: 'German',
  nl: 'Nederlands',
  it: 'Italian',
  ja: 'Japanese',
  ko: 'Korean',
  br: 'Portuguese',
  es: 'Spanish',
};

const MappedSearchResult = ({
  pageModels,
  contentButtons,
  config: searchConfig = {},
  labels,
  presentation,
  content,
  detailContent,
  sortBy: sortOptions = [],
  categoryUrl = {},
  contentCategoryCardFieldMapper = {},
}: any) => {
  const {
    config,
    esLabels,
    language,
    taxonomy: taxonomies,
    contentCategoryCardDateFieldMapper,
  } = pageModels || {};
  const { isAuthenticated } = useIsAuthenticated();

  const brPage = useContext(BrPageContext);
  const { elasticsearch: elasticsearchConfig } = config || {};
  const {
    search_key: searchKey,
    global_engine: engine,
    global_auth_engine: authEngine,
    engines_query_sorting_blacklist: sortingBlacklist,
  } = elasticsearchConfig || {};
  const { taxonomyFacets, additionalFacets } = content;
  const { searchResults = 10, 'variant-label': datalayerVariant } = presentation;
  const [ableToSearch, setAbleToSearch] = useState(false);
  const isFirstLoad = useRef(true);
  const queryParams = useParams();

  useBotSearchParams();

  const facets: string[] = React.useMemo(
    () => [...(additionalFacets || []), ...(taxonomyFacets || [])],
    [additionalFacets, taxonomyFacets],
  );

  const defaultParams = React.useRef({
    q: '',
    keywords: '',
    page: '1',
    sortBy: 'most-relevant',
    size: String(parseInt(searchResults, 10)),
    filters: {},
  });

  const parsedQueryParams = qs.parse(window.location.search);

  const {
    q = defaultParams.current.q,
    keywords = defaultParams.current.keywords,
    page = defaultParams.current.page,
    sortBy = defaultParams.current.sortBy,
    size = defaultParams.current.size,
    ...filters
  } = parsedQueryParams;

  // Search Params
  const [search, setSearch] = useState({
    q,
    keywords,
    page,
    sortBy,
    size,
    filters: encodeWithCustomFilters({
      filters,
      facets,
      customsFilters: pick(['category', 'is_partner_only', 'tax_language'], parsedQueryParams),
    }),
    needsPush: true,
  });

  const isSortShown = useMemo(
    () => !(readCsvLine({ data: sortingBlacklist }).includes(engine) && search?.q),
    [engine, sortingBlacklist, search?.q],
  );

  const elasticSearchFields = useMemo(
    () =>
      !isAuthenticated
        ? SEARCH_RESULT_ELASTIC_DEFAULT_FIELDS
        : merge(SEARCH_RESULT_ELASTIC_DEFAULT_FIELDS, elasticSearchAuthDefaultFields),
    [isAuthenticated],
  );

  const es = useElasticSearch({
    engine,
    authEngine,
    searchParams: search,
    facets: partnerOnlyFacetsWithLanguageModifier(filters, facets),
    initBodyFilters: ['is_partner_only'],
    useDynamicFacets: false,
    searchKey,
    filterType: 'all',
    parseSortBy: parseSortByPublicationDate,
    fields: elasticSearchFields,
    language,
    labels: esLabels,
  });

  // Reload Facets when is_partner_only changes
  useEffect(() => {
    es.reloadFacets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedQueryParams.is_partner_only]);

  const setQueryParam = useCallback(
    (key: string, value: any, isFilter?: boolean): void => {
      try {
        if (isFilter) {
          setSearch((data: any) => {
            // Logic modified to address for tax_language auto-selection
            // when is_partner_only is selected and to remove both if deselected
            const {
              tax_language: taxLanguage,
              is_partner_only: isPartnerOnly,
              ...dataFilters
            } = data.filters;

            let nextFilters = dataFilters;

            if (key === 'is_partner_only') {
              if (value[0] === 'true') {
                nextFilters = {
                  ...dataFilters,
                  is_partner_only: ['true'],
                  tax_language: [TAX_LANGUAGE_MAP[language]],
                };
              }
            } else {
              nextFilters = {
                ...data.filters,
                [key]: value,
              };
            }

            return {
              ...data,
              page: '1',
              filters: nextFilters,
              needsPush: true,
            };
          });
        } else {
          setSearch((data: any) => ({
            ...data,
            [key]: value,
            needsPush: true,
          }));
        }
      } catch (e) {
        console.log('e', { e });
      }
    },
    [language],
  );

  React.useEffect(() => {
    if (isSortShown) return;

    setQueryParam('sortBy', defaultParams.current.sortBy);
  }, [isSortShown, setQueryParam, defaultParams.current.sortBy]);

  useEffect(() => {
    if (ableToSearch && search.needsPush) {
      // eslint-disable-next-line no-restricted-globals
      history.pushState({}, '', `?${qs.stringify(ElasticAdapter.sanitizeParams(search))}`);
    }
    setAbleToSearch(true);
    // eslint-disable-next-line
  }, [search]);

  const results = useMemo(
    () =>
      es.response.results
        ? adaptResults({
            results: es.response.results,
            page: brPage,
            config: { ...searchConfig, language },
            taxonomies,
            contentCategoryCardFieldMapper,
            contentCategoryCardDateFieldMapper,
          })
        : [],
    [
      brPage,
      es.response.results,
      searchConfig,
      language,
      taxonomies,
      contentCategoryCardFieldMapper,
      contentCategoryCardDateFieldMapper,
    ],
  );

  React.useEffect(() => {
    const qParams = qs.parse(window.location.search);

    const {
      q: newQuery,
      keywords: newKeywords,
      page: newPage,
      sortBy: newSortBy,
      size: newSize,
      ...newFilters
    } = qParams;

    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    // History change from Header (Search).
    if (!qParams.q) {
      return;
    }

    setSearch({
      ...defaultParams.current,
      ...qParams,
      filters: encodeWithCustomFilters({
        filters: newFilters,
        facets: partnerOnlyFacetsWithLanguageModifier(newFilters, facets),
        customsFilters: pick(['category', 'is_partner_only', 'tax_language'], qParams),
      }),
      needsPush: false,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const allLabels = React.useMemo(() => ({ ...labels, ...taxonomies }), [labels, taxonomies]);

  const { loading } = LoadingStatusValues;
  const [status, setStatus] = useState<ElasticComponentContextType['status']>(loading);
  useEffect(() => resolveStatus({ rawVal: es, setStatus, isSearch: true }), [es]);
  const searchResultContent = useMemo(
    () => ({ ...detailContent, isCached: es.isCached }),
    [detailContent, es.isCached],
  );
  const searchPresentation = useMemo(() => ({ isSortShown }), [isSortShown]);

  return (
    <ContentContainer
      topSpacing={SpacingSizes.small}
      mobileTopSpacing={SpacingSizes.none}
      bottomSpacing={SpacingSizes.small}
      topBackgroundColor={ColorNames.eggshell}
      bottomBackgroundColor={ColorNames.eggshell}
      datalayerPosition="search_result"
      datalayerVariant={datalayerVariant}
    >
      <MuiContainer>
        <ElasticComponentContext.Provider
          value={{
            queryParams: search,
            updateQueryParams: setQueryParam,
            sortOptions,
            pagination: es.response.pagination,
            labels: allLabels,
            facets: es.facets,
            status,
            results,
            engine,
            requestID: es.response?.meta?.request_id,
            searchKey,
          }}
        >
          <SearchResult
            content={searchResultContent}
            labels={allLabels}
            contentButtons={contentButtons}
            categoryUrl={categoryUrl}
            presentation={searchPresentation}
          />
        </ElasticComponentContext.Provider>
      </MuiContainer>
    </ContentContainer>
  );
};

const MappedSearchResultGTM = withGTMPipe(
  {
    onDatalayer,
    config: { mapTarget },
  },
  {
    onDatalayer: onDatalayerCustomFilters,
    config: {
      eventName: 'gtm:custom:filters',
      isCustom: true,
    },
  },
  {
    onDatalayer: onDatalayerCustomSort,
    config: {
      eventName: 'gtm:custom:sort',
      isCustom: true,
    },
  },
  {
    onDatalayer: onDatalayerTriggerSearchResult,
    config: {
      eventName: 'gtm:custom:query-search',
      isCustom: true,
    },
  },
  {
    onDatalayer: onDatalayerDownloadFile,
    config: {
      eventName: 'gtm:custom:download-file',
      isCustom: true,
    },
  },
)(MappedSearchResult);

const SearchResultCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    'search.labels': 'labels',
    'search.config': 'config',
    componentParameterMap: 'presentation',
    document: [
      'content',
      {
        // Taxonomy integration.
        taxonomyTopLevel: ['taxonomyFacets', resolveTaxonomyFacets],
        additionalfilters: 'additionalFacets',
      },
    ],
    descriptiveDocument: [
      'detailContent',
      {
        detailContent: [null, resolveRTC],
      },
    ],
    'value-lists/search-sort-by': ['sortBy', resolveSortBy],
    'value-lists/search-category-url': 'categoryUrl',
    'taxonomies---content-category-card-field-mapper': 'contentCategoryCardFieldMapper',
  });

  return <CMSWrapper brProps={brProps} render={MappedSearchResultGTM} mapping={mapping.current} />;
};

export default SearchResultCMSWrapper;
